import React, { useCallback, useEffect, useRef, useState } from "react";
import DatePickerPopover from "./DatePickerPopover";
import { fromISO, toISO } from "./dateUtils";

// =============================================================================
// Troca o calendário NATIVO de todos os <input type="date"> e
// <input type="datetime-local"> do sistema pelo DatePickerPanel — sem mexer em
// nenhuma tela: o clique (ou toque) no campo abre o calendário novo e a escolha
// é gravada no próprio <input>, disparando `input`/`change`, então todo
// `onChange`/`register` do React segue igual.
//
// Fica fora: campos desabilitados/somente leitura e os marcados com
// `data-native-date`. Não há botão OK: clicar no dia já grava e fecha. Em
// `datetime-local` a hora é escolhida na linha "Hora" do calendário: mudar a hora
// com uma data já escolhida grava na hora, sem fechar. A digitação direta continua
// pelo teclado (Tab até o campo); o calendário em si não tem campo de digitar.
// Montado UMA vez em index.js.
// =============================================================================

const SELECTOR = 'input[type="date"], input[type="datetime-local"]';
const TOUCH_MOVE_TOLERANCE = 10;
const TOUCH_GHOST_MS = 800;

const GUARD_ATTR = "data-dp-guard";
const GUARD_MS = 800;

// `preventDefault` em mousedown/click/touchend nem sempre basta: no modo
// responsivo do F12 (Chrome/Firefox) e em alguns navegadores móveis o calendário
// NATIVO ainda abre por outro caminho (foco, toque emulado, clique no ícone).
// Campo somente leitura não abre o nativo em nenhum deles, então, no instante do
// ponteiro, o campo vira `readonly` por ~0,8 s (o suficiente para o gesto
// terminar) e volta ao normal. O atributo `data-dp-guard` distingue o
// readonly temporário do readonly de verdade da tela.
const guardTimers = new WeakMap();
const guardNative = (input) => {
  if (!input.readOnly) {
    input.readOnly = true;
    input.setAttribute(GUARD_ATTR, "");
  }
  clearTimeout(guardTimers.get(input));
  guardTimers.set(
    input,
    setTimeout(() => {
      if (input.hasAttribute(GUARD_ATTR)) {
        input.readOnly = false;
        input.removeAttribute(GUARD_ATTR);
      }
    }, GUARD_MS),
  );
};

const eligible = (input) =>
  Boolean(input) &&
  !input.disabled &&
  (!input.readOnly || input.hasAttribute(GUARD_ATTR)) &&
  !input.hasAttribute("data-native-date");

const setNativeValue = (input, value) => {
  const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, "value").set;
  setter.call(input, value);
  input.dispatchEvent(new Event("input", { bubbles: true }));
  input.dispatchEvent(new Event("change", { bubbles: true }));
};

export default function DatePickerHost() {
  const [state, setState] = useState(null); // { input, kind, time }
  const stateRef = useRef(null);
  const lastTouchRef = useRef(0);
  stateRef.current = state;

  const close = useCallback(() => setState(null), []);

  const open = useCallback((input) => {
    if (stateRef.current && stateRef.current.input === input) {
      setState(null);
      return;
    }
    const kind = input.type === "datetime-local" ? "datetime-local" : "date";
    const time = kind === "datetime-local" ? String(input.value || "").slice(11, 16) : "";
    setState({ input, kind, time });
  }, []);

  useEffect(() => {
    const findInput = (target) =>
      target instanceof Element ? target.closest(SELECTOR) : null;

    // Primeiro evento de qualquer gesto (mouse, toque ou caneta): já blinda.
    const onPointerDown = (event) => {
      const input = findInput(event.target);
      if (eligible(input)) guardNative(input);
    };
    const onMouseDown = (event) => {
      if (event.button !== 0) return;
      const input = findInput(event.target);
      if (!eligible(input)) return;
      guardNative(input);
      if (Date.now() - lastTouchRef.current < TOUCH_GHOST_MS) {
        event.preventDefault();
        return;
      }
      event.preventDefault(); // impede o calendário nativo
      input.focus({ preventScroll: true });
      open(input);
    };
    // Alguns navegadores abrem o nativo no `click`/pelo ícone: bloqueia também.
    const onClick = (event) => {
      const input = findInput(event.target);
      if (!eligible(input)) return;
      guardNative(input);
      event.preventDefault();
    };
    const onKeyDown = (event) => {
      const input = findInput(event.target);
      if (!eligible(input)) return;
      if ((event.altKey && event.key === "ArrowDown") || event.key === "F4") {
        event.preventDefault();
        open(input);
      }
    };

    // Toque: um toque parado abre; arrastar continua rolando a página.
    let pending = null;
    const onTouchStart = (event) => {
      pending = null;
      const input = findInput(event.target);
      if (event.touches.length === 1 && eligible(input)) {
        guardNative(input);
        pending = { input, x: event.touches[0].clientX, y: event.touches[0].clientY };
      }
    };
    const onTouchMove = (event) => {
      if (!pending || !event.touches.length) return;
      const dx = event.touches[0].clientX - pending.x;
      const dy = event.touches[0].clientY - pending.y;
      if (Math.hypot(dx, dy) > TOUCH_MOVE_TOLERANCE) pending = null;
    };
    const onTouchEnd = (event) => {
      if (!pending) return;
      const { input } = pending;
      pending = null;
      guardNative(input);
      if (event.cancelable) event.preventDefault();
      lastTouchRef.current = Date.now();
      open(input);
    };

    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("mousedown", onMouseDown, true);
    document.addEventListener("click", onClick, true);
    document.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("touchstart", onTouchStart, { capture: true, passive: true });
    document.addEventListener("touchmove", onTouchMove, { capture: true, passive: true });
    document.addEventListener("touchend", onTouchEnd, { capture: true, passive: false });
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("mousedown", onMouseDown, true);
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("touchstart", onTouchStart, true);
      document.removeEventListener("touchmove", onTouchMove, true);
      document.removeEventListener("touchend", onTouchEnd, true);
    };
  }, [open]);

  if (!state) return <DatePickerPopover open={false} onClose={close} />;

  const { input, kind, time } = state;
  const current = fromISO(String(input.value || "").slice(0, 10));
  const min = fromISO(input.min);
  const max = fromISO(input.max);

  const commit = (date) => {
    const iso = toISO(date);
    setNativeValue(input, kind === "datetime-local" ? `${iso}T${time || "00:00"}` : iso);
    setState(null);
    input.focus({ preventScroll: true });
  };
  const clear = () => {
    setNativeValue(input, "");
    setState(null);
    input.focus({ preventScroll: true });
  };

  return (
    <DatePickerPopover
      key={input.name || input.id || "dp"}
      open
      anchor={input}
      mode="single"
      value={current}
      min={min}
      max={max}
      allowClear={!input.required}
      title={kind === "datetime-local" ? "Selecionar data e hora" : "Selecionar data"}
      onConfirm={commit}
      onClear={clear}
      onClose={close}
      extra={
        kind === "datetime-local" ? (
          <div className="dp-time">
            <label>
              <span>Hora</span>
              <input
                type="time"
                value={time}
                data-native-date=""
                onChange={(e) => {
                  const next = e.target.value;
                  setState((s) => (s ? { ...s, time: next } : s));
                  // Já há data no campo: a hora nova vale na hora (o calendário não fecha).
                  if (current && next) setNativeValue(input, `${toISO(current)}T${next}`);
                }}
              />
            </label>
          </div>
        ) : null
      }
    />
  );
}
